import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Coins, X, Tag, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { QuickContactModal } from "./QuickContactModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FinanceModal = ({
  isOpen,
  onClose,
  onSubmit,
  members = [],
  contacts = [],
  costCenters = [],
  bankAccounts = [],
}) => {
  const [accountType, setAccountType] = useState("conta_corrente_loja");
  const [bankAccountId, setBankAccountId] = useState("");
  const [txType, setTxType] = useState("receita");
  const [category, setCategory] = useState("Mensalidade/Capitação");
  const [subcategory, setSubcategory] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [memberId, setMemberId] = useState("");
  const [contactId, setContactId] = useState("");
  const [costCenterId, setCostCenterId] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [txStatus, setTxStatus] = useState("pago");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [isQuickContactOpen, setIsQuickContactOpen] = useState(false);
  const [localContacts, setLocalContacts] = useState(contacts);

  const [catalog, setCatalog] = useState({});

  useEffect(() => {
    setLocalContacts(contacts);
  }, [contacts]);

  useEffect(() => {
    if (!isOpen) return;
    // Reset when opened
    setSubcategory("");
    setTagsInput("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setDueDate("");
    setNeedsReview(false);
    // Auto-selecionar a conta corrente padrão (ou primeira ativa)
    const defaultCC = bankAccounts.find(b => b.seed_key === "conta_corrente_loja") || bankAccounts.find(b => b.is_active !== false);
    if (defaultCC) setBankAccountId(defaultCC.id);
  }, [isOpen, bankAccounts]);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/finance/subcategories`)
      .then((response) => {
        const nextCatalog = response.data?.catalog || {};
        setCatalog(nextCatalog);
        setCategory((currentCategory) => {
          if (Object.prototype.hasOwnProperty.call(nextCatalog, currentCategory)) return currentCategory;
          setSubcategory("");
          return Object.keys(nextCatalog)[0] || "Outros";
        });
      })
      .catch(() => setCatalog({}));
  }, [isOpen, txType]);

  const subcategoryOptions = useMemo(() => {
    return catalog[category] || [];
  }, [catalog, category]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    if (!description.trim()) {
      toast.error("Informe a descrição do lançamento.");
      return;
    }

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);

    const payload = {
      account_type: accountType,
      bank_account_id: needsReview ? null : (bankAccountId || null),
      needs_review: needsReview,
      type: txType,
      category,
      subcategory: subcategory || null,
      amount: val,
      description: description.trim(),
      payment_method: paymentMethod,
      member_id: memberId || null,
      contact_id: contactId || null,
      cost_center_id: costCenterId || null,
      tags,
      status: txStatus,
      payment_date: txStatus === "pago" ? paymentDate : null,
      due_date: dueDate || null,
    };

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-finance-form"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Coins className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">Lançamento Financeiro</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Conta / Banco</label>
              <select
                data-testid="select-finance-bank-account"
                value={bankAccountId}
                onChange={(e) => {
                  const id = e.target.value;
                  setBankAccountId(id);
                  const ba = bankAccounts.find(b => b.id === id);
                  // Sincroniza account_type legado (compatibilidade com stats antigos)
                  if (ba?.seed_key === "tronco_beneficencia") setAccountType("tronco_beneficencia");
                  else setAccountType("conta_corrente_loja");
                }}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                {bankAccounts.filter(b => b.is_active !== false).map((ba) => (
                  <option key={ba.id} value={ba.id}>
                    {ba.name}{ba.bank_name ? ` (${ba.bank_name})` : ""}
                  </option>
                ))}
                {bankAccounts.length === 0 && (
                  <option value="">— Cadastre uma conta primeiro —</option>
                )}
              </select>
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Tipo de Fluxo</label>
              <div className="flex p-1 bg-[#080C14] rounded-xl border border-slate-700">
                <button
                  type="button"
                  data-testid="finance-type-receita"
                  onClick={() => setTxType("receita")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    txType === "receita" ? "bg-emerald-500 text-slate-950" : "text-slate-400"
                  }`}
                >
                  Receita (+)
                </button>
                <button
                  type="button"
                  data-testid="finance-type-despesa"
                  onClick={() => setTxType("despesa")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    txType === "despesa" ? "bg-rose-500 text-white" : "text-slate-400"
                  }`}
                >
                  Despesa (-)
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Categoria</label>
              <select
                data-testid="select-finance-category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubcategory("");
                }}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                {Object.keys(catalog).length > 0
                  ? Object.keys(catalog).map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))
                  : (
                      <>
                        <option value="Mensalidade/Capitação">Mensalidade / Capitação</option>
                        <option value="Tronco de Beneficência">Tronco de Beneficência</option>
                        <option value="Aluguel/Manutenção Templo">Aluguel / Manutenção Templo</option>
                        <option value="Material Litúrgico/Ágape">Material Litúrgico / Ágape</option>
                        <option value="Grande Oriente/Potência">Repasse Potência</option>
                        <option value="Auxílio Filantrópico">Auxílio Filantrópico</option>
                        <option value="Doação">Doação</option>
                        <option value="Outros">Outros</option>
                      </>
                    )}
              </select>
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Subcategoria</label>
              <input
                data-testid="input-finance-subcategory"
                list="subcategory-options"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                placeholder="Escolha ou digite…"
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
              <datalist id="subcategory-options">
                {subcategoryOptions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Valor (R$) *</label>
              <input
                data-testid="input-finance-amount"
                type="number"
                step="0.01"
                required
                placeholder="180,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-[#080C14] text-[#D4AF37] font-mono font-bold p-2.5 rounded-xl border border-slate-700 text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Forma de Pagamento</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="A definir">A definir (não sei ainda)</option>
                <option value="PIX">PIX</option>
                <option value="Boleto">Boleto Bancário</option>
                <option value="Dinheiro">Dinheiro em Espécie</option>
                <option value="Transferência TED">Transferência TED</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Descrição *</label>
            <input
              data-testid="input-finance-description"
              type="text"
              required
              placeholder="Ex: Aluguel do Templo mês de maio"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Irmão (opcional)</label>
              <select
                data-testid="select-finance-member"
                value={memberId}
                onChange={(e) => {
                  setMemberId(e.target.value);
                    if (e.target.value) {
                      setContactId("");
                      setTxStatus("pendente");
                    }
                }}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="">Nenhum</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} (CIM: {m.cim})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">
                Cliente / Fornecedor (opcional)
              </label>
              <div className="flex gap-2">
                <select
                  data-testid="select-finance-contact"
                  value={contactId}
                  onChange={(e) => {
                    setContactId(e.target.value);
                    if (e.target.value) {
                      setMemberId("");
                      setTxStatus("pendente");
                    }
                  }}
                  className="flex-1 bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
                >
                  <option value="">Nenhum</option>
                  {localContacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.kind})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="btn-open-quick-contact"
                  onClick={() => setIsQuickContactOpen(true)}
                  title="Cadastrar novo fornecedor/cliente"
                  className="shrink-0 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/40 text-[#D4AF37] rounded-xl px-3 border border-[#D4AF37]/40 transition-all flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span className="text-[10px] uppercase font-bold tracking-wider">Novo</span>
                </button>
              </div>
            </div>
          </div>

          {memberId && (
            <div
              data-testid="finance-member-account-impact"
              className={`rounded-lg border px-3 py-2 text-xs ${
                txStatus === "pago"
                  ? "border-slate-600 bg-slate-800/40 text-slate-300"
                  : txType === "receita"
                    ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                    : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              }`}
            >
              {txStatus === "pago"
                ? "Lançamento quitado: ficará no histórico e não alterará o saldo aberto do Irmão."
                : txType === "receita"
                  ? "Receita pendente: cria um DÉBITO na conta do Irmão — ele deve este valor à Loja."
                  : "Despesa pendente: cria um CRÉDITO na conta do Irmão — a Loja deve este valor a ele."}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Centro de Custo</label>
              <select
                data-testid="select-finance-cc"
                value={costCenterId}
                onChange={(e) => setCostCenterId(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="">Nenhum</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.code} — {cc.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300 flex items-center gap-1">
                <Tag className="w-3 h-3" />
                Tags (separadas por vírgula)
              </label>
              <input
                data-testid="input-finance-tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="ex: templo, evento, urgente"
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-800">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Status</label>
              <div className="flex p-1 bg-[#080C14] rounded-xl border border-slate-700">
                <button
                  type="button"
                  data-testid="btn-status-pago"
                  onClick={() => setTxStatus("pago")}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                    txStatus === "pago" ? "bg-emerald-500 text-slate-950" : "text-slate-400"
                  }`}
                >
                  Pago (emitir recibo)
                </button>
                <button
                  type="button"
                  data-testid="btn-status-pendente"
                  onClick={() => setTxStatus("pendente")}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                    txStatus === "pendente" ? "bg-amber-500 text-slate-950" : "text-slate-400"
                  }`}
                >
                  Pendente
                </button>
              </div>
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Data do Pagamento</label>
              <input
                data-testid="input-finance-payment-date"
                type="date"
                disabled={txStatus !== "pago"}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs disabled:opacity-40"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Vencimento</label>
              <input
                data-testid="input-finance-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          {txStatus === "pago" && !needsReview && (
            <div className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
              ✓ Recibo será emitido automaticamente com numeração sequencial da Loja.
            </div>
          )}

          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/5 border border-amber-500/30">
            <input
              id="needs-review-checkbox"
              data-testid="input-needs-review"
              type="checkbox"
              checked={needsReview}
              onChange={(e) => setNeedsReview(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-500"
            />
            <label htmlFor="needs-review-checkbox" className="text-[11px] text-slate-300 cursor-pointer">
              <span className="font-semibold text-amber-300">📌 Deixar pendente de classificação</span>
              <span className="block text-slate-400 mt-0.5">
                Use quando não souber a conta bancária ou categoria correta agora. O lançamento vai para a aba <b>Pendentes</b> e não conta no saldo até ser classificado.
              </span>
            </label>
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-finance"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              Confirmar Lançamento
            </button>
          </div>
        </form>
      </div>

      <QuickContactModal
        isOpen={isQuickContactOpen}
        onClose={() => setIsQuickContactOpen(false)}
        defaultKind={txType === "receita" ? "cliente" : "fornecedor"}
        members={members}
        onCreated={(entity) => {
          if (entity.entity_type === "member") {
            setMemberId(entity.id);
            setContactId("");
            setTxStatus("pendente");
          } else {
            setLocalContacts((prev) => [...prev, entity]);
            setContactId(entity.id);
            setMemberId("");
            setTxStatus("pendente");
          }
        }}
      />
    </div>
  );
};
